import { Link } from 'react-router-dom'
import SEOHead, { buildBreadcrumbs } from '../components/SEOHead'

const SITE_URL = 'https://nala.vet'

// ─── Placeholder articles — replaced by API data once blog-infra is live ──────
const PLACEHOLDER_ARTICLES = [
  {
    slug: 'signs-your-dog-needs-a-vet',
    title: '10 Signs Your Dog Needs a Vet Visit Today',
    excerpt:
      'Most dogs are stoic by nature — they hide pain and discomfort until it becomes serious. Here are the warning signs every dog owner should know.',
    category: 'Dog Health',
    readTime: '5 min read',
  },
  {
    slug: 'puppy-vaccination-schedule',
    title: 'The Complete Puppy Vaccination Schedule',
    excerpt:
      'Vaccinations are the cornerstone of preventive care for puppies. This guide covers what vaccines are needed, when, and why — following AAHA guidelines.',
    category: 'Preventive Care',
    readTime: '7 min read',
  },
  {
    slug: 'what-to-expect-home-vet-visit',
    title: 'What to Expect from Your First Home Vet Visit',
    excerpt:
      'Wondering how a house-call vet visit works? We walk you through exactly what happens from the moment your Nala vet arrives at your door.',
    category: 'About Nala',
    readTime: '4 min read',
  },
  {
    slug: 'senior-pet-care-tips',
    title: 'Senior Pet Care: Keeping Older Pets Healthy and Happy',
    excerpt:
      'Pets aged 7+ have different health needs. From diet and exercise to twice-yearly exams, here\'s how to give your senior pet the best quality of life.',
    category: 'Senior Pets',
    readTime: '6 min read',
  },
  {
    slug: 'cat-stress-free-vet-visit',
    title: 'Why In-Home Vet Visits Are Better for Cats',
    excerpt:
      'Cats are highly sensitive to environmental stress. A 2019 study found that 58% of cat owners avoided vet visits due to the stress it caused their cat. There\'s a better way.',
    category: 'Cat Health',
    readTime: '5 min read',
  },
  {
    slug: 'end-of-life-care-pets',
    title: 'In-Home End-of-Life Care: A Compassionate Choice',
    excerpt:
      'When it\'s time to say goodbye, doing so at home — surrounded by family in a familiar environment — can be a profound gift for both pets and their owners.',
    category: 'End-of-Life Care',
    readTime: '8 min read',
  },
]

export default function BlogPage() {
  return (
    <>
      <SEOHead
        title="Veterinary Health Blog"
        description="Expert pet health articles written by licensed Nala veterinarians. Learn about preventive care, common conditions, and how to keep your pets healthy."
        canonical={`${SITE_URL}/blog`}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Blog',
            name: 'Nala Veterinary Health Blog',
            description: 'Expert pet health content written by licensed veterinarians.',
            url: `${SITE_URL}/blog`,
            publisher: {
              '@type': 'Organization',
              name: 'Nala',
              url: SITE_URL,
            },
          },
          buildBreadcrumbs([{ name: 'Blog', url: `${SITE_URL}/blog` }]),
        ]}
      />

      {/* Header */}
      <div className="bg-gradient-to-br from-brand-50 to-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-brand-600 font-semibold text-sm uppercase tracking-widest mb-3">
            Veterinary Health Blog
          </p>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Expert pet health advice
          </h1>
          <p className="text-lg text-gray-600 max-w-xl mx-auto">
            Written by licensed Nala veterinarians. Evidence-based, practical,
            and designed to help your pets live their best lives.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {PLACEHOLDER_ARTICLES.map((article) => (
            <article
              key={article.slug}
              className="card flex flex-col hover:shadow-md transition-shadow group"
            >
              <div className="mb-3">
                <span className="inline-block text-xs font-semibold text-brand-600 bg-brand-50 px-3 py-1 rounded-full">
                  {article.category}
                </span>
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-brand-700 transition-colors leading-snug">
                {article.title}
              </h2>
              <p className="text-sm text-gray-500 flex-1 leading-relaxed">
                {article.excerpt}
              </p>
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                <span className="text-xs text-gray-400">{article.readTime}</span>
                <span className="text-xs text-brand-600 font-medium">Coming soon →</span>
              </div>
            </article>
          ))}
        </div>

        {/* Newsletter CTA */}
        <div className="mt-20 card bg-brand-50 border-brand-100 text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Get pet health tips in your inbox
          </h2>
          <p className="text-gray-600 mb-6 text-sm max-w-md mx-auto">
            Monthly articles written by our vets. No spam — just practical advice
            for keeping your pets healthy.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-sm mx-auto">
            <input
              type="email"
              placeholder="you@example.com"
              className="input flex-1"
              aria-label="Email address for newsletter"
            />
            <button className="btn-primary whitespace-nowrap">Subscribe</button>
          </div>
          <p className="text-xs text-gray-400 mt-3">Unsubscribe any time.</p>
        </div>

        {/* CTA to book */}
        <div className="text-center mt-16">
          <p className="text-gray-600 mb-4">Ready to get expert care for your pet?</p>
          <Link to="/book" className="btn-primary">
            Book a Home Visit
          </Link>
        </div>
      </div>
    </>
  )
}
